import { apiClient } from 'lib/api';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { MutationConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import type { AdSetEntry, AdSettingEntry } from 'features/tiktok/api';

export interface CreativePack {
  creative_names: string[];
  tag: string;
}

export interface CreateTikTokAdParams {
  app: string;
  ad_account_id: string;
  creative_packs: CreativePack[];
  campaign_templates: AdSetEntry[];
  ad_settings: AdSettingEntry[];
}

export interface CreateTikTokAdResponse {
  job_id: string;
}

const createTikTokAd = async (
  body: CreateTikTokAdParams,
): Promise<CreateTikTokAdResponse> => {
  const res = await apiClient.post<CreateTikTokAdResponse>(
    queryKeys.createAd,
    body,
  );

  if (isAxiosError(res)) {
    const error = new Error('Axios error');
    error.cause = {
      ...res.response?.data,
      status: res.response?.status,
    };
    return Promise.reject(error);
  }
  return res.data;
};

interface UseCreateTikTokAdOptions {
  config?: MutationConfig<typeof createTikTokAd>;
}

const useCreateTikTokAd = ({ config }: UseCreateTikTokAdOptions = {}) => {
  return useMutation({
    ...config,
    onSuccess: (...args) => {
      if (config?.onSuccess) {
        config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: createTikTokAd,
  });
};

export { createTikTokAd, useCreateTikTokAd };
